var _excluded = ["onChange", "onBlur", "onFocus", "defaultValue", "currency", "helperText", "editButtonTooltip", "integerLimitMask", "errorMessage", "allowNegativeValue", "descriptionAlignment"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import MaskedInput from 'react-text-mask';
import { Group } from '../../../containers';
import { useIntl } from '../../../theme/i18n';
import { forwardRef } from '../../../utils';
import { createNumberMask } from '../../../utils/create-number-mask-utils';
import { convertCurrencyToNumber, defaultCurrency } from '../../../utils/currency-utils';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { IconButton } from '../../action';
import { Tooltip, Typography } from '../../dataDisplay';
import { Box, ConditionalWrapper } from '../../internal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var AmountInput = forwardRef((_ref, ref) => {
  var _formatCurrency;

  var {
    onChange,
    onBlur,
    onFocus: _onFocus,
    defaultValue,
    currency = defaultCurrency,
    helperText,
    editButtonTooltip,
    integerLimitMask,
    errorMessage,
    allowNegativeValue = true,
    descriptionAlignment = 'center'
  } = _ref,
      rest = _objectWithoutProperties(_ref, _excluded);

  var {
    formatCurrency
  } = useIntl();

  var format = value => formatCurrency(value);

  var [inputValue, setInputValue] = useState((_formatCurrency = formatCurrency(defaultValue)) !== null && _formatCurrency !== void 0 ? _formatCurrency : '');
  var [placeholder, setPlaceholder] = useState(formatCurrency(defaultValue, currency));
  var [isEditMode, setIsEditMode] = useState(false);
  var [isFocus, setIsFocus] = useState(false);
  var inputWidthInChars = inputValue.length > 0 ? inputValue.length : placeholder.length;
  var styles = useMultiStyleConfig('AmountInput', {
    inputWidthInChars,
    descriptionAlignment
  });
  var inputRef = useRef(null);
  useImperativeHandle(ref, () => {
    var _inputRef$current;

    return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.inputElement;
  });
  var currencyMask = createNumberMask({
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    integerLimit: integerLimitMask !== null && integerLimitMask !== void 0 ? integerLimitMask : defaultValue.toFixed(0).length,
    allowNegative: allowNegativeValue,
    allowLeadingZeroes: true
  });
  useEffect(() => {
    if (errorMessage) {
      setIsEditMode(true);
    }
  }, [errorMessage]);

  var handleChange = event => {
    if (event.target.value === '') return;
    setInputValue(event.target.value);
    setPlaceholder(event.target.value);
    var parsedValue = convertCurrencyToNumber(event.target.value).toString();
    onChange === null || onChange === void 0 ? void 0 : onChange({
      target: {
        value: parsedValue
      }
    });
  };

  var handleBlur = e => {
    var {
      value
    } = e.target;
    var parsedValue = convertCurrencyToNumber(value.length ? value : placeholder);
    var formattedValue = format(parsedValue);
    setInputValue(formattedValue);
    setPlaceholder(formattedValue);

    if (!errorMessage) {
      setIsFocus(false);
      setIsEditMode(false);
    }

    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  };

  var handleEditMode = () => {
    var _inputRef$current2;

    setIsFocus(true);
    setIsEditMode(true);
    setInputValue('');
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.inputElement.focus();
  };

  var handleKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      var _inputRef$current3;

      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.inputElement.blur();
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [inputValue]);

  var editButton = /*#__PURE__*/_jsx(IconButton, {
    "aria-label": "amount-edit-button",
    "data-testid": "amount-edit-button",
    icon: "edit",
    variant: "primary",
    onClick: handleEditMode
  });

  var showHelperText = !errorMessage && helperText && !isEditMode;
  return /*#__PURE__*/_jsxs(Group, _objectSpread(_objectSpread({
    variant: "vertical",
    justifyContent: "center",
    spacing: "xs",
    width: "fit-content",
    "data-component": "AmountInput"
  }, rest), {}, {
    children: [/*#__PURE__*/_jsxs(Box, {
      __css: styles['field'],
      children: [isEditMode || errorMessage ? /*#__PURE__*/_jsx(Box // ts-ignore is used due to `type` issues with `<Box/>`.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      , {
        type: "text",
        value: inputValue,
        guide: false,
        "aria-label": "amount input",
        "data-testid": "amount-input",
        placeholder: placeholder,
        onChange: handleChange,
        sx: styles['inputElement'] // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        ,
        ref: inputRef,
        as: MaskedInput,
        mask: currencyMask,
        onBlur: handleBlur,
        onFocus: e => {
          setIsFocus(true);
          _onFocus === null || _onFocus === void 0 ? void 0 : _onFocus(e);
        },
        onKeyDown: handleKeyDown,
        "data-view-mode": !isEditMode && !errorMessage || null,
        autoFocus: isFocus
      }) : /*#__PURE__*/_jsx(Box, {
        as: "span",
        sx: styles['inputDisplay'],
        "data-testid": "amount-input-display",
        children: inputValue
      }), !isEditMode && !errorMessage && /*#__PURE__*/_jsx(ConditionalWrapper, {
        condition: Boolean(editButtonTooltip),
        wrapper: children => /*#__PURE__*/_jsx(Tooltip, {
          label: editButtonTooltip,
          children: children
        }),
        children: editButton
      })]
    }), (errorMessage || showHelperText) && /*#__PURE__*/_jsxs(Box, {
      sx: styles['description'],
      children: [errorMessage && /*#__PURE__*/_jsx(Typography.Description, {
        label: errorMessage,
        isInvalid: true,
        "data-testid": "amount-input-error-message"
      }), showHelperText && /*#__PURE__*/_jsx(Typography.Description, {
        label: helperText,
        "data-testid": "amount-input-helper-text"
      })]
    })]
  }));
});
AmountInput.displayName = 'AmountInput';
//# sourceMappingURL=AmountInput.js.map